/**
 * `/customers/balance-alerts` (#1892) — the operator surface for the customer
 * balance alert.
 *
 * #1876 shipped an automatic mailer with no instrument and no brake. This page
 * is the four things that were missing, and they are one page rather than four
 * because each is useless alone: a switch with no history is a lever nobody
 * knows when to pull; a history with no forecast says who was mailed but not
 * who is next; a forecast with no per-account mute leaves "stop bothering this
 * one customer" as an all-or-nothing choice.
 *
 * Layout is deliberate. The SWITCH is always on screen, above everything, in
 * its own block — it ships closed, so "off" is the ordinary state of a freshly
 * promoted environment and the whole point of the default evaporates if that
 * state is quiet. The other three are tabs, and the forecast and the record
 * are two of them rather than one merged table: one is what will happen, the
 * other is what did, and a reader who conflates them acts on the wrong tense.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useT } from '../i18n/index.ts';
import { ACCOUNT_BALANCE_ALERT_HISTORY_LIMIT } from '../api/accountBalanceAlerts.ts';
import {
  useAccountBalanceAlertHistory,
  useAccountBalanceAlertPreview,
  useAccountBalanceAlertSettings,
  useAccountBalanceAlertSuppressions,
  useCreateAccountBalanceAlertSuppression,
  useDeleteAccountBalanceAlertSuppression,
  useUpdateAccountBalanceAlertSettings,
} from '../api/useAccountBalanceAlerts.ts';
import { BalanceAlertHistoryTable } from '../components/balance-alert/BalanceAlertHistoryTable.tsx';
import { BalanceAlertPreviewTable } from '../components/balance-alert/BalanceAlertPreviewTable.tsx';
import {
  BalanceAlertRestoreDialog,
  BalanceAlertSuppressDialog,
} from '../components/balance-alert/BalanceAlertSuppressionDialogs.tsx';
import { BalanceAlertSuppressionsTable } from '../components/balance-alert/BalanceAlertSuppressionsTable.tsx';
import { BalanceAlertSwitchPanel } from '../components/balance-alert/BalanceAlertSwitchPanel.tsx';
import { useToast } from '../components/Toast.tsx';
import {
  Button,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/index.ts';

const TAB_PREVIEW = 'preview';
const TAB_HISTORY = 'history';
const TAB_SUPPRESSIONS = 'suppressions';

interface SuppressTarget {
  readonly accountId: string;
  readonly email: string;
}

export function BalanceAlertsPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [tab, setTab] = useState<string>(TAB_PREVIEW);
  const [suppressTarget, setSuppressTarget] = useState<SuppressTarget | null>(null);
  const [suppressReason, setSuppressReason] = useState('');
  const [restoreTarget, setRestoreTarget] = useState<string | null>(null);

  const settings = useAccountBalanceAlertSettings();
  const preview = useAccountBalanceAlertPreview();
  const history = useAccountBalanceAlertHistory();
  const suppressions = useAccountBalanceAlertSuppressions();

  const updateSettings = useUpdateAccountBalanceAlertSettings();
  const createSuppression = useCreateAccountBalanceAlertSuppression();
  const deleteSuppression = useDeleteAccountBalanceAlertSuppression();

  const fail = (error: unknown): void => {
    toast.error(error instanceof Error ? error.message : t('balance_alerts.error.generic'));
  };

  const submitSwitch = (next: { readonly enabled: boolean; readonly reason: string | null }): void => {
    updateSettings.mutate(next, {
      onSuccess: (result) => {
        toast.success(
          t(result.enabled ? 'balance_alerts.toast.enabled' : 'balance_alerts.toast.disabled'),
        );
      },
      onError: fail,
    });
  };

  const confirmSuppress = (): void => {
    if (suppressTarget === null) return;
    const target = suppressTarget;
    const reason = suppressReason.trim();
    createSuppression.mutate(
      { accountId: target.accountId, reason: reason.length > 0 ? reason : null },
      {
        onSuccess: (result) => {
          // The cleared dedup row is reported rather than left to be
          // discovered: suppressing an account mid-incident ENDS that
          // incident's state, and `first_alerted_at` goes with it.
          toast.success(
            t(
              result.cleared_active_alert
                ? 'balance_alerts.toast.suppressed_cleared'
                : 'balance_alerts.toast.suppressed',
              { email: target.email },
            ),
          );
        },
        onError: fail,
      },
    );
    setSuppressTarget(null);
    setSuppressReason('');
  };

  const confirmRestore = (): void => {
    if (restoreTarget === null) return;
    deleteSuppression.mutate(restoreTarget, {
      onSuccess: (result) => {
        toast.success(
          t(result.removed ? 'balance_alerts.toast.restored' : 'balance_alerts.toast.restore_noop'),
        );
      },
      onError: fail,
    });
    setRestoreTarget(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('balance_alerts.title')}
        sub={t('balance_alerts.subtitle')}
        testId="balance-alerts-header"
      />

      <BalanceAlertSwitchPanel
        settings={settings.data ?? null}
        loading={settings.isLoading}
        error={settings.isError}
        saving={updateSettings.isPending}
        onRetry={() => void settings.refetch()}
        onSubmit={submitSwitch}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList ariaLabel={t('balance_alerts.tabs_label')}>
          <TabsTrigger value={TAB_PREVIEW} testId="balance-alerts-tab-preview">
            {t('balance_alerts.tab.preview')}
          </TabsTrigger>
          <TabsTrigger value={TAB_HISTORY} testId="balance-alerts-tab-history">
            {t('balance_alerts.tab.history')}
          </TabsTrigger>
          <TabsTrigger value={TAB_SUPPRESSIONS} testId="balance-alerts-tab-suppressions">
            {t('balance_alerts.tab.suppressions')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={TAB_PREVIEW} className="mt-4">
          <Panel
            caption={t('balance_alerts.panel.preview')}
            count={preview.data?.entries.length}
            testId="balance-alerts-preview-panel"
            padded
            actions={
              <Button
                size="sm"
                variant="ghost"
                loading={preview.isFetching}
                onClick={() => void preview.refetch()}
                data-testid="balance-alerts-preview-refresh"
              >
                {t('balance_alerts.preview.recompute')}
              </Button>
            }
          >
            <QueryBody
              loading={preview.isLoading}
              error={preview.isError}
              onRetry={() => void preview.refetch()}
              testId="balance-alerts-preview"
            >
              {preview.data === undefined ? null : (
                <BalanceAlertPreviewTable
                  preview={preview.data}
                  suppressingAccountId={
                    createSuppression.isPending ? createSuppression.variables?.accountId ?? null : null
                  }
                  onSuppress={(accountId, email) => {
                    setSuppressReason('');
                    setSuppressTarget({ accountId, email });
                  }}
                />
              )}
            </QueryBody>
          </Panel>
        </TabsContent>

        <TabsContent value={TAB_HISTORY} className="mt-4">
          <Panel
            caption={t('balance_alerts.panel.history')}
            count={history.data?.length}
            testId="balance-alerts-history-panel"
            padded
          >
            <QueryBody
              loading={history.isLoading}
              error={history.isError}
              onRetry={() => void history.refetch()}
              testId="balance-alerts-history"
            >
              <BalanceAlertHistoryTable
                entries={history.data ?? []}
                limit={ACCOUNT_BALANCE_ALERT_HISTORY_LIMIT}
              />
            </QueryBody>
          </Panel>
        </TabsContent>

        <TabsContent value={TAB_SUPPRESSIONS} className="mt-4">
          <Panel
            caption={t('balance_alerts.panel.suppressions')}
            count={suppressions.data?.length}
            testId="balance-alerts-suppressions-panel"
            padded
          >
            <QueryBody
              loading={suppressions.isLoading}
              error={suppressions.isError}
              onRetry={() => void suppressions.refetch()}
              testId="balance-alerts-suppressions"
            >
              <BalanceAlertSuppressionsTable
                suppressions={suppressions.data ?? []}
                restoringAccountId={
                  deleteSuppression.isPending ? deleteSuppression.variables ?? null : null
                }
                onRestore={setRestoreTarget}
              />
            </QueryBody>
          </Panel>
        </TabsContent>
      </Tabs>

      <BalanceAlertSuppressDialog
        open={suppressTarget !== null}
        onOpenChange={(open) => {
          if (!open) setSuppressTarget(null);
        }}
        email={suppressTarget?.email ?? ''}
        loading={createSuppression.isPending}
        reasonField={
          <FormField
            label={t('balance_alerts.suppress.reason_label')}
            fieldId="balance-alerts-suppress-reason"
            hint={t('balance_alerts.suppress.reason_hint')}
          >
            <Input
              id="balance-alerts-suppress-reason"
              value={suppressReason}
              onChange={(event) => setSuppressReason(event.currentTarget.value)}
              data-testid="balance-alerts-suppress-reason"
            />
          </FormField>
        }
        onConfirm={confirmSuppress}
      />

      <BalanceAlertRestoreDialog
        open={restoreTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRestoreTarget(null);
        }}
        loading={deleteSuppression.isPending}
        onConfirm={confirmRestore}
      />
    </div>
  );
}

/**
 * Loading / error / content, in one place so the three tabs cannot each invent
 * a different answer to "the endpoint did not respond".
 */
function QueryBody({
  loading,
  error,
  onRetry,
  testId,
  children,
}: {
  readonly loading: boolean;
  readonly error: boolean;
  readonly onRetry: () => void;
  readonly testId: string;
  readonly children: ReactNode;
}): JSX.Element {
  const t = useT();
  if (loading) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (error) {
    return (
      <EmptyState
        title={t('balance_alerts.error.load')}
        hint={t('balance_alerts.error.load_hint')}
        action={
          <Button variant="secondary" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        }
        testId={`${testId}-error`}
      />
    );
  }
  return <>{children}</>;
}
